import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Search, Clock, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { apiRequest } from "@/lib/queryClient";

interface TerrainsClub {
  name: string;
  city: string;
  department?: string;
  location?: { lat: number; lng: number };
  bookingUrl: string;
}

interface TerrainsSlot {
  club: string;
  city: string;
  department?: string;
  terrain: string;
  indoor: boolean;
  slot: string;
  durationMin: number;
  pricePerPlayer: number;
  provider: "doinsport" | "playtomic";
  available: boolean;
  bookingUrl: string;
}

interface SlotsResult {
  date: string;
  from: string;
  to: string;
  count: number;
  slots: TerrainsSlot[];
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function formatPrice(centimes: number) {
  return (centimes / 100).toFixed(2).replace(".", ",") + " €";
}

function groupByClub(slots: TerrainsSlot[]): Record<string, TerrainsSlot[]> {
  return slots.reduce(
    (acc, slot) => {
      if (!acc[slot.club]) acc[slot.club] = [];
      acc[slot.club].push(slot);
      return acc;
    },
    {} as Record<string, TerrainsSlot[]>
  );
}

// Departments derived from clubs
function getDepartments(clubs: TerrainsClub[]): { value: string; label: string }[] {
  const depts = new Map<string, string>();
  clubs.forEach((c) => {
    if (c.department) {
      depts.set(c.department, c.department);
    }
  });
  return Array.from(depts.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([value, label]) => ({ value, label }));
}

export default function TerrainsView() {
  const { t } = useTranslation();

  // Persistent state
  const [date, setDate] = useState(() => todayStr());
  const [from, setFrom] = useState(() => localStorage.getItem("terrains_from") || "08:00");
  const [to, setTo] = useState(() => localStorage.getItem("terrains_to") || "22:00");
  const [department, setDepartment] = useState(() => localStorage.getItem("terrains_dept") || "");
  const [selectedClubs, setSelectedClubs] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem("terrains_clubs") || "[]");
    } catch {
      return [];
    }
  });
  const [openClubs, setOpenClubs] = useState<Set<string>>(new Set());
  const [clubDropdownOpen, setClubDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Search trigger
  const [searchKey, setSearchKey] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);

  // Persist preferences
  useEffect(() => { localStorage.setItem("terrains_from", from); }, [from]);
  useEffect(() => { localStorage.setItem("terrains_to", to); }, [to]);
  useEffect(() => { localStorage.setItem("terrains_dept", department); }, [department]);
  useEffect(() => { localStorage.setItem("terrains_clubs", JSON.stringify(selectedClubs)); }, [selectedClubs]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setClubDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const { data: clubs = [] } = useQuery<TerrainsClub[]>({
    queryKey: ["/api/terrains/clubs"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/terrains/clubs");
      return res.json();
    },
    staleTime: 5 * 60_000,
  });

  const filteredClubs = department
    ? clubs.filter((c) => c.department === department)
    : clubs;

  const { data: result, isLoading, isFetching, isError } = useQuery<SlotsResult>({
    queryKey: ["/api/terrains/slots", searchKey, date, from, to, department, selectedClubs.join(",")],
    queryFn: async () => {
      const params = new URLSearchParams({ date, from, to });
      if (department) params.set("department", department);
      if (selectedClubs.length > 0) params.set("clubs", selectedClubs.join(","));
      const res = await apiRequest("GET", `/api/terrains/slots?${params.toString()}`);
      return res.json();
    },
    enabled: hasSearched && searchKey > 0,
    staleTime: 5 * 60_000,
  });

  const handleSearch = () => {
    setHasSearched(true);
    setSearchKey((k) => k + 1);
  };

  const toggleClubOpen = (clubName: string) => {
    setOpenClubs((prev) => {
      const next = new Set(prev);
      if (next.has(clubName)) next.delete(clubName);
      else next.add(clubName);
      return next;
    });
  };

  const toggleClubSelection = (clubName: string) => {
    setSelectedClubs((prev) =>
      prev.includes(clubName) ? prev.filter((c) => c !== clubName) : [...prev, clubName]
    );
  };

  const grouped = result ? groupByClub(result.slots) : {};
  const departments = getDepartments(clubs);

  return (
    <div className="bg-gray-50 dark:bg-muted min-h-full pb-20">
      {/* Filter bar */}
      <div className="bg-white dark:bg-card px-4 pt-4 pb-3 shadow-sm border-b border-border space-y-2">
        {/* Row 1: date + heures */}
        <div className="flex gap-2 items-center">
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="flex-1 bg-gray-100 dark:bg-input border-gray-300 dark:border-border h-9 text-xs [color-scheme:light] dark:[color-scheme:dark]"
          />
          <Input
            type="time"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="w-24 bg-gray-100 dark:bg-input border-gray-300 dark:border-border h-9 text-xs [color-scheme:light] dark:[color-scheme:dark]"
          />
          <span className="text-muted-foreground text-xs">→</span>
          <Input
            type="time"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-24 bg-gray-100 dark:bg-input border-gray-300 dark:border-border h-9 text-xs [color-scheme:light] dark:[color-scheme:dark]"
          />
        </div>

        {/* Row 2: department + clubs dropdown + search button */}
        <div className="flex gap-2 items-center">
          {/* Department select */}
          <select
            value={department}
            onChange={(e) => {
              setDepartment(e.target.value);
              setSelectedClubs([]);
            }}
            className="w-[120px] flex-shrink-0 h-9 text-xs rounded-md border border-gray-300 dark:border-border bg-gray-100 dark:bg-input px-2 text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          >
            <option value="">{t("terrains.allDepts", "Tous les dép.")}</option>
            {departments.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>

          {/* Clubs multi-select dropdown */}
          <div className="relative flex-1 min-w-0" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setClubDropdownOpen((o) => !o)}
              className="w-full h-9 text-xs rounded-md border border-gray-300 dark:border-border bg-gray-100 dark:bg-input px-2 text-left text-foreground flex items-center justify-between hover:bg-gray-200 dark:hover:bg-muted transition-colors"
            >
              <span className="truncate">
                {selectedClubs.length === 0 || selectedClubs.length === filteredClubs.length
                  ? t("terrains.allClubs", "Tous les clubs")
                  : `${selectedClubs.length} club(s)`}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 ml-1" />
            </button>

            {clubDropdownOpen && (
              <div className="absolute left-0 top-full mt-1 z-30 bg-white dark:bg-popover border border-gray-300 dark:border-border rounded-lg shadow-lg w-64 max-h-60 overflow-y-auto">
                <div className="p-1">
                  <button
                    className="w-full text-left text-xs px-3 py-2 rounded hover:bg-gray-50 dark:hover:bg-muted text-blue-700 dark:text-blue-400 font-medium"
                    onClick={() => {
                      const allSelected = selectedClubs.length === filteredClubs.length;
                      setSelectedClubs(allSelected ? [] : filteredClubs.map((c) => c.name));
                    }}
                  >
                    {selectedClubs.length === filteredClubs.length
                      ? t("terrains.deselectAll", "Tout désélectionner")
                      : t("terrains.allClubs", "Tous les clubs")}
                  </button>
                  {filteredClubs.map((club) => (
                    <label
                      key={club.name}
                      className="flex items-center gap-2 text-xs px-3 py-2 rounded hover:bg-gray-50 dark:hover:bg-muted cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedClubs.includes(club.name)}
                        onChange={() => toggleClubSelection(club.name)}
                        className="accent-[#001489]"
                      />
                      <span className="truncate">{club.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Search button */}
          <Button
            onClick={handleSearch}
            disabled={isFetching}
            className="h-9 px-3 bg-[#001489] hover:bg-[#001489]/90 text-white flex-shrink-0"
          >
            <Search className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Results */}
      <div className="p-4">
        {(isLoading || isFetching) && (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-lg" />
            ))}
          </div>
        )}

        {!isFetching && isError && (
          <div className="text-center py-10 text-muted-foreground text-sm">
            {t("terrains.error", "Impossible de charger les créneaux. Vérifiez votre connexion.")}
          </div>
        )}

        {!isFetching && !isError && hasSearched && result && result.count === 0 && (
          <div className="text-center py-10 text-muted-foreground text-sm">
            {t("terrains.noResults", "Aucun créneau disponible pour ces critères.")}
          </div>
        )}

        {!isFetching && !isError && !hasSearched && (
          <div className="text-center py-14 text-muted-foreground text-sm flex flex-col items-center gap-3">
            <Search className="w-10 h-10 text-gray-300 dark:text-muted-foreground" />
            <p>{t("terrains.hint", "Sélectionne une date et lance la recherche.")}</p>
          </div>
        )}

        {!isFetching && result && result.count > 0 && (
          <>
            <p className="text-xs text-muted-foreground mb-3">
              {result.count} {t("terrains.slots", "créneau(x)")} disponible(s)
            </p>

            <div className="space-y-2">
              {Object.entries(grouped).map(([clubName, slots]) => {
                const isOpen = openClubs.has(clubName);
                return (
                  <div
                    key={clubName}
                    className="bg-white dark:bg-card rounded-lg shadow-sm border border-gray-100 dark:border-border overflow-hidden"
                  >
                    {/* Club header */}
                    <button
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-muted transition-colors"
                      onClick={() => toggleClubOpen(clubName)}
                    >
                      <div className="text-left">
                        <div className="font-semibold text-foreground text-sm">{clubName}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {slots[0].city} · {slots.length} {t("terrains.slots", "créneau(x)")}
                        </div>
                      </div>
                      {isOpen ? (
                        <ChevronUp className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      )}
                    </button>

                    {/* Slots list */}
                    {isOpen && (
                      <div className="border-t border-gray-50 dark:border-border divide-y divide-gray-50 dark:divide-border">
                        {slots.map((s, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between px-4 py-3 bg-gray-50/30 dark:bg-transparent"
                          >
                            <div className="flex items-center gap-3">
                              {/* Time */}
                              <div className="text-lg font-bold text-foreground tabular-nums w-12">
                                {s.slot}
                              </div>
                              {/* Details */}
                              <div>
                                <div className="text-sm font-medium text-foreground">{s.terrain}</div>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span
                                    className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                      s.indoor
                                        ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                        : "bg-gray-100 dark:bg-muted text-gray-500 dark:text-muted-foreground"
                                    }`}
                                  >
                                    {s.indoor
                                      ? t("terrains.indoor", "Indoor")
                                      : t("terrains.outdoor", "Outdoor")}
                                  </span>
                                  <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                                    <Clock className="w-3 h-3" />
                                    {s.durationMin} min
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Price + book */}
                            <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                              <div className="text-sm font-bold text-[#001489] dark:text-blue-400">
                                {formatPrice(s.pricePerPlayer)}
                                <span className="text-[10px] font-normal text-muted-foreground">
                                  {" "}{t("terrains.perPlayer", "/joueur")}
                                </span>
                              </div>
                              <button
                                onClick={() => window.open(s.bookingUrl, "_blank", "noopener")}
                                className="flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded bg-[#001489] text-white hover:bg-[#001489]/90 transition-colors"
                              >
                                {t("terrains.book", "Réserver")}
                                <ExternalLink className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
